import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelValueSource, LabelValueAcceptedBy } from 'graphql/types';

import { useCustomLabelArray } from 'modules/customLabels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { CustomLabel } from '../labelSchema';

type useBulkCustomSuggestionsActionsProps = {
  fieldName: string;
};
type useBulkCustomSuggestionsActionsResult = {
  enableCustomActions: boolean;
  denyAllCustomLabelSuggestionHandler: () => void;
  acceptAllCustomLabelSuggestionHandler: () => void;
};
export const useBulkCustomSuggestionsActions = ({
  fieldName
}: useBulkCustomSuggestionsActionsProps): useBulkCustomSuggestionsActionsResult => {
  const { control } = useFormContext();
  const { replace } = useCustomLabelArray({ fieldName });
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );

  const fields = useWatch({
    name: `${fieldName}`,
    control
  });

  const enableCustomActions = useMemo(
    () =>
      (fields ?? []).some(
        (field: CustomLabel) =>
          field?.valueSource === LabelValueSource.Suggestion
      ),
    [fields]
  );

  const denyAllCustomLabelSuggestionHandler = useCallback(() => {
    const userFields = (fields ?? []).filter((field: CustomLabel) => {
      if (field?.valueSource === LabelValueSource.User && field.value) {
        return field;
      }
    });
    replace?.(userFields);
  }, [fields, replace]);

  const acceptAllCustomLabelSuggestionHandler = useCallback(() => {
    const userField = (fields ?? []).map((field: CustomLabel) => {
      if (
        field.valueSource === LabelValueSource.Suggestion &&
        field.value &&
        isLabelLevelAcceptedByEnabled === false
      ) {
        return {
          ...field,
          valueSource: LabelValueSource.User
        };
      } else if (isLabelLevelAcceptedByEnabled && field.value) {
        return {
          ...field,
          valueSource: field.valueSource ?? LabelValueSource.User,
          acceptedBy: field.acceptedBy ?? LabelValueAcceptedBy.User
        };
      } else {
        return field;
      }
    });
    replace?.(userField);
  }, [fields, replace, isLabelLevelAcceptedByEnabled]);

  return {
    denyAllCustomLabelSuggestionHandler,
    enableCustomActions,
    acceptAllCustomLabelSuggestionHandler
  };
};
